<template>
  <div id="language-translations">
    <v-container fluid>
      <v-flex xs12>
        <div class="option-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <v-icon class="option-icon mr-2" :color="iconColor">
                mdi-translate
              </v-icon>
              Translations for:
              {{ languageInfo?.label || languageCode?.toUpperCase() }}
            </h1>
          </div>

          <!-- Back to Languages Button and View Toggle -->
          <v-flex class="d-flex align-center justify-space-between mb-7 mt-12">
            <div class="d-flex align-center">
              <v-btn
                color="primary"
                variant="outlined"
                class="mr-4"
                @click="goBackToLanguages"
              >
                <v-icon class="mr-2">mdi-arrow-left</v-icon>
                Back to Languages
              </v-btn>

              <!-- View Toggle -->
              <v-btn-toggle
                v-model="viewMode"
                color="primary"
                variant="outlined"
                class="mr-4"
              >
                <v-btn value="tree" size="small">
                  <v-icon class="mr-1">mdi-file-tree</v-icon>
                  Tree View
                </v-btn>
                <v-btn value="json" size="small">
                  <v-icon class="mr-1">mdi-code-json</v-icon>
                  JSON View
                </v-btn>
              </v-btn-toggle>
            </div>

            <v-btn
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold"
              @click="saveTranslations"
              :loading="saving"
              :disabled="!hasChanges"
            >
              Save Changes
            </v-btn>
          </v-flex>

          <!-- Loading and Error States -->
          <v-alert v-if="loading" type="info" class="mb-4">
            Loading translations...
          </v-alert>

          <v-alert v-if="error" type="error" class="mb-4">
            {{ error }}
          </v-alert>

          <!-- Unsaved Changes Alert -->
          <v-alert v-if="hasChanges" type="warning" class="mb-4" prominent>
            <template #prepend>
              <v-icon>mdi-alert-circle</v-icon>
            </template>
            <div>
              <div class="mb-2">
                <strong
                  >You have unsaved changes. Don't forget to save your
                  work!</strong
                >
              </div>
              <div class="d-flex align-center justify-space-between">
                <span class="text-body-2"
                  >{{ changedFields.length }} field(s) modified</span
                >
                <v-btn
                  color="warning"
                  variant="outlined"
                  size="small"
                  @click="saveTranslations"
                  :loading="saving"
                >
                  Save Now
                </v-btn>
              </div>
            </div>
          </v-alert>

          <!-- JSON View -->
          <div
            v-if="!loading && !error && translationsData && viewMode === 'json'"
          >
            <v-card class="mb-6">
              <v-card-title class="text-h6">
                <v-icon class="mr-2">mdi-code-json</v-icon>
                JSON Editor
              </v-card-title>
              <v-card-text>
                <JsonViewer
                  :data="translationsData"
                  :has-changes="hasChanges"
                  :original-value="originalTranslationsData"
                  @update="updateTranslationValue"
                />
              </v-card-text>
            </v-card>
          </div>

          <!-- Tree View -->
          <div
            v-if="!loading && !error && translationsData && viewMode === 'tree'"
          >
            <!-- Dynamic Sections -->
            <template
              v-for="(section, index) in availableSections"
              :key="section.key"
            >
              <!-- Category Header -->
              <div
                v-if="
                  index === 0 ||
                  availableSections[index - 1].category !== section.category
                "
                class="text-h5 font-weight-bold mb-4 mt-6 category-header text-grey-darken-4"
              >
                {{
                  section.category === 'global'
                    ? 'Global Elements'
                    : section.category === 'pages'
                    ? 'Page-Specific Elements'
                    : section.category === 'system'
                    ? 'System/Admin Elements'
                    : 'Other Elements'
                }}
              </div>
              <v-card
                v-if="section.value && Object.keys(section.value).length > 0"
                class="mb-6"
                :class="{
                  'section-with-changes': sectionsWithChanges.has(section.key),
                }"
              >
                <v-card-title class="text-h6">
                  <v-icon class="mr-2" size="x-small">{{
                    section.icon
                  }}</v-icon>
                  {{ section.title }}
                  <v-chip
                    v-if="sectionsWithChanges.has(section.key)"
                    color="warning"
                    size="x-small"
                    class="ml-2"
                  >
                    <v-icon size="12" class="mr-1">mdi-pencil</v-icon>
                    Modified
                  </v-chip>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    variant="outlined"
                    size="small"
                    @click="toggleSection(section.key)"
                  >
                    {{
                      expandedSections[section.key]
                        ? 'Collapse All'
                        : 'Expand All'
                    }}
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <TranslationTreeItem
                    :value="section.value"
                    :key-path="section.key"
                    :depth="0"
                    :force-expanded="expandedSections[section.key]"
                    :has-changes="hasChanges"
                    :original-value="getOriginalValue(section.key)"
                    @update="updateTranslationValue"
                  />
                </v-card-text>
              </v-card>
            </template>
          </div>

          <!-- No translations found -->
          <div
            v-if="!loading && !error && !translationsData"
            class="text-center py-8"
          >
            <v-icon size="64" color="grey">mdi-translate-off</v-icon>
            <p class="text-h6 mt-4">No translations found</p>
            <p class="text-body-2 text-grey">
              This language doesn't have any translations yet.
            </p>
          </div>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import merge from 'lodash/merge'
  import db from '@/firebase/init'
  import JsonViewer from './JsonViewer.vue'
  import TranslationTreeItem from './TranslationTreeItem.vue'

  const route = useRoute()
  const router = useRouter()

  const languageCode = route.params.code
  const languageInfo = ref(null)
  const translationsData = ref(null)
  const loading = ref(false)
  const saving = ref(false)
  const error = ref(null)
  const hasChanges = computed(() => {
    return changedFields.value.length > 0
  })
  const viewMode = ref('tree') // Default to tree view
  const originalTranslationsData = ref(null)

  // Dynamic section expansion states
  const expandedSections = ref({})

  // Track which sections have changes for auto-expansion
  const sectionsWithChanges = ref(new Set())

  const iconColor = computed(() => {
    return '#592892' // Primary color
  })

  /**
   * Shipped `/public/locales/*.json`. Merged here so admins see keys added in-repo
   * before they exist in `uiTranslations` (Firestore keys are not auto-updated on deploy).
   */
  const fetchBundledLocaleForAdmin = async (code) => {
    if (!code || typeof code !== 'string') return null
    try {
      const res = await fetch(
        `/locales/${encodeURIComponent(code)}.json`,
        { cache: 'no-store', credentials: 'same-origin' }
      )
      if (!res.ok) return null
      const data = await res.json()
      return data && typeof data === 'object' ? data : null
    } catch {
      return null
    }
  }

  const loadLanguageInfo = async () => {
    try {
      const doc = await db.collection('languages').doc(languageCode).get()
      if (doc.exists) {
        languageInfo.value = { id: doc.id, ...doc.data() }
      }
    } catch (err) {
      console.error('Error loading language info:', err)
    }
  }

  const loadTranslations = async () => {
    loading.value = true
    error.value = null

    try {
      const [bundledLocale, doc] = await Promise.all([
        fetchBundledLocaleForAdmin(languageCode),
        db.collection('uiTranslations').doc(languageCode).get(),
      ])

      let mergedTranslations = null

      if (doc.exists) {
        const data = doc.data()
        const fromFirestore =
          data != null &&
          data.translations != null &&
          typeof data.translations === 'object'
            ? data.translations
            : data
        mergedTranslations = merge(
          {},
          bundledLocale && typeof bundledLocale === 'object' ? bundledLocale : {},
          fromFirestore && typeof fromFirestore === 'object' ? fromFirestore : {}
        )
      } else if (bundledLocale && typeof bundledLocale === 'object') {
        mergedTranslations = JSON.parse(JSON.stringify(bundledLocale))
      }

      if (mergedTranslations && typeof mergedTranslations === 'object') {
        translationsData.value = mergedTranslations
        originalTranslationsData.value = JSON.parse(
          JSON.stringify(translationsData.value)
        )
      } else {
        error.value =
          bundledLocale === null && !doc.exists
            ? `No bundled locale at /locales/${languageCode}.json and no Firestore uiTranslations document.`
            : 'No translations found for this language'
      }
    } catch (err) {
      console.error('Error loading translations:', err)
      error.value = 'Failed to load translations'
    } finally {
      loading.value = false
    }
  }

  // Helper function to get original value for a key path
  const getOriginalValue = (keyPath) => {
    if (!originalTranslationsData.value || !keyPath) return null

    const keys = keyPath.split('.')
    let current = originalTranslationsData.value

    for (let i = 0; i < keys.length; i++) {
      if (current && typeof current === 'object' && keys[i] in current) {
        current = current[keys[i]]
      } else {
        return null
      }
    }

    return current
  }

  // Helper function to find all changed fields
  const getChangedFields = () => {
    if (!translationsData.value || !originalTranslationsData.value) return []

    const changedFields = []

    const findChanges = (current, original, path = '') => {
      if (typeof current !== 'object' || current === null) {
        // This is a leaf node (primitive value)
        if (JSON.stringify(current) !== JSON.stringify(original)) {
          changedFields.push(path)
        }
        return
      }

      // This is an object, check its children
      for (const key in current) {
        if (Object.prototype.hasOwnProperty.call(current, key)) {
          const newPath = path ? `${path}.${key}` : key
          const originalValue =
            original && typeof original === 'object' ? original[key] : undefined
          findChanges(current[key], originalValue, newPath)
        }
      }
    }

    findChanges(translationsData.value, originalTranslationsData.value)
    return changedFields
  }

  // Computed property for changed fields
  const changedFields = computed(() => {
    return getChangedFields()
  })

  // Auto-expand sections with changes when switching to tree view
  watch(
    [changedFields, viewMode],
    ([fields, mode]) => {
      if (mode === 'tree' && fields.length > 0) {
        const sectionsToExpand = new Set()

        // Find which sections contain changed fields
        fields.forEach((fieldPath) => {
          const sectionKey = fieldPath.split('.')[0]
          if (
            sectionKey &&
            availableSections.value.some((s) => s.key === sectionKey)
          ) {
            sectionsToExpand.add(sectionKey)
            sectionsWithChanges.value.add(sectionKey)
          }
        })

        // Auto-expand sections with changes
        sectionsToExpand.forEach((sectionKey) => {
          expandedSections.value[sectionKey] = true
          console.log(
            `[Auto-expand] Expanded section '${sectionKey}' due to changes`
          )
        })
      } else if (fields.length === 0) {
        // Clear sections with changes when no changes remain
        sectionsWithChanges.value.clear()
      }
    },
    { immediate: true }
  )

  // Dynamic sections for tree view
  const availableSections = computed(() => {
    if (!translationsData.value) return []

    const sections = []
    // Organized section configuration with categories and alphabetical sorting
    const sectionConfig = {
      // GLOBAL/COMMON SECTIONS (used across multiple pages) - Alphabetical order
      auth: {
        icon: 'mdi-login',
        title: 'Authentication',
        category: 'global',
        order: 1,
      },
      common: {
        icon: 'mdi-widgets',
        title: 'Common UI Elements',
        category: 'global',
        order: 2,
      },
      dates: {
        icon: 'mdi-calendar',
        title: 'Date Formats',
        category: 'global',
        order: 3,
      },
      howto: {
        icon: 'mdi-help-circle',
        title: 'How To Use This Page',
        category: 'global',
        order: 4,
      },
      navbar: {
        icon: 'mdi-navigation',
        title: 'Navigation Bar',
        category: 'global',
        order: 5,
      },
      profile: {
        icon: 'mdi-account-edit',
        title: 'Profile Editor',
        category: 'global',
        order: 6,
      },
      title: {
        icon: 'mdi-format-title',
        title: 'Page Titles',
        category: 'global',
        order: 7,
      },
      validation: {
        icon: 'mdi-check-circle',
        title: 'Validation Messages',
        category: 'global',
        order: 8,
      },
      years: {
        icon: 'mdi-calendar-range',
        title: 'Years',
        category: 'global',
        order: 9,
      },

      // PAGE-SPECIFIC SECTIONS (alphabetical order)
      // To add a new page:
      // 1. Add the section here in alphabetical order
      // 2. Update the order numbers accordingly
      // 3. Use appropriate mdi icon
      // 4. Set category: 'pages'
      api: {
        icon: 'mdi-key',
        title: 'API Key',
        category: 'pages',
        order: 1,
      },
      courses: {
        icon: 'mdi-book-open-variant',
        title: 'Courses',
        category: 'pages',
        order: 2,
      },
      groups: {
        icon: 'mdi-account-group',
        title: 'Groups',
        category: 'pages',
        order: 3,
      },
      dashboard: {
        icon: 'mdi-view-dashboard',
        title: 'Dashboard',
        category: 'pages',
        order: 4,
      },
      feedback: {
        icon: 'mdi-comment-text-outline',
        title: 'Feedback',
        category: 'pages',
        order: 5,
      },
      missions: {
        icon: 'mdi-folder-multiple',
        title: 'Missions',
        category: 'pages',
        order: 6,
      },
      notifications: {
        icon: 'mdi-bell-outline',
        title: 'Notifications',
        category: 'pages',
        order: 7,
      },
      pendingUsers: {
        icon: 'mdi-account-clock',
        title: 'Pending Users',
        category: 'pages',
        order: 8,
      },
      sections: {
        icon: 'mdi-folder-open',
        title: 'Sections',
        category: 'pages',
        order: 9,
      },
      terms: {
        icon: 'mdi-file-document-outline',
        title: 'Terms',
        category: 'pages',
        order: 10,
      },
      users: {
        icon: 'mdi-account-group',
        title: 'Users',
        category: 'pages',
        order: 11,
      },
      welcome: {
        icon: 'mdi-home',
        title: 'Welcome Page',
        category: 'pages',
        order: 12,
      },

      // PLACEHOLDER SECTIONS FOR UPCOMING PAGES
      // Uncomment and update when implementing these pages:

      // groups: {
      //   icon: 'mdi-account-group',
      //   title: 'Groups',
      //   category: 'pages',
      //   order: 10
      // },
      // media: {
      //   icon: 'mdi-image-multiple',
      //   title: 'Media',
      //   category: 'pages',
      //   order: 11
      // },
      // reports: {
      //   icon: 'mdi-chart-line',
      //   title: 'Reports',
      //   category: 'pages',
      //   order: 12
      // },
      units: {
        icon: 'mdi-file-document',
        title: 'Units',
        category: 'pages',
        order: 13,
      },
      unitBuilder: {
        icon: 'mdi-tools',
        title: 'Unit Builder',
        category: 'pages',
        order: 14,
      },
      unitStudy: {
        icon: 'mdi-book-open-page-variant',
        title: 'Unit Study',
        category: 'pages',
        order: 15,
      },
      courseDrawer: {
        icon: 'mdi-menu',
        title: 'Course Drawer',
        category: 'pages',
        order: 16,
      },
      // users: {
      //   icon: 'mdi-account-multiple',
      //   title: 'Users',
      //   category: 'pages',
      //   order: 15
      // },
      weeks: {
        icon: 'mdi-calendar-week',
        title: 'Weeks',
        category: 'pages',
        order: 17,
      },

      // SYSTEM/ADMIN SECTIONS
      adminLanguages: {
        icon: 'mdi-cog',
        title: 'Admin Languages Interface',
        category: 'system',
        order: 1,
      },
      tourSteps: {
        icon: 'mdi-map-marker-path',
        title: 'Tour Steps',
        category: 'system',
        order: 2,
      },
      uiTranslations: {
        icon: 'mdi-translate',
        title: 'UI Translations',
        category: 'system',
        order: 3,
      },
    }

    for (const [key, value] of Object.entries(translationsData.value)) {
      if (value && typeof value === 'object' && !key.includes('.')) {
        const config = sectionConfig[key] || {
          icon: 'mdi-folder',
          title: key,
          category: 'other',
          order: 999,
        }
        sections.push({
          key,
          value,
          icon: config.icon,
          title: config.title,
          category: config.category,
          order: config.order,
        })
      }
    }

    // Sort sections by category, then by order within each category
    return sections.sort((a, b) => {
      const categoryOrder = { global: 1, pages: 2, system: 3, other: 4 }
      const categoryCompare =
        categoryOrder[a.category] - categoryOrder[b.category]
      if (categoryCompare !== 0) return categoryCompare
      return a.order - b.order
    })
  })

  const updateTranslationValue = (keyPath, newValue) => {
    console.log('updateTranslationValue called with:', { keyPath, newValue })
    if (!translationsData.value) return
    if (!keyPath) {
      console.error('keyPath is undefined or empty:', keyPath)
      return
    }

    // Parse the keyPath to handle both dot notation and array notation
    // e.g., "tourSteps[0].title" or "tourSteps.0.title"
    let keys = []
    let currentPath = keyPath

    // Handle array notation like [0] and convert to dot notation
    currentPath = currentPath.replace(/\[(\d+)\]/g, '.$1')
    keys = currentPath.split('.')

    console.log('Parsed keys:', keys)
    let current = translationsData.value

    // Navigate to the parent of the target property
    for (let i = 0; i < keys.length - 1; i++) {
      if (current[keys[i]] === undefined) {
        current[keys[i]] = {}
      }
      current = current[keys[i]]
    }

    // Update the final property
    current[keys[keys.length - 1]] = newValue
  }

  const saveTranslations = async () => {
    if (!translationsData.value) return

    saving.value = true
    error.value = null

    try {
      await db.collection('uiTranslations').doc(languageCode).set(
        {
          translations: translationsData.value,
          updatedAt: new Date(),
        },
        { merge: true }
      )

      // Update original data to current data after successful save
      originalTranslationsData.value = JSON.parse(
        JSON.stringify(translationsData.value)
      )
      console.log('Translations saved successfully')

      // Force refresh of UI translations cache
      console.log(
        'Dispatching translationsUpdated event for language:',
        languageCode
      )
      window.dispatchEvent(
        new CustomEvent('translationsUpdated', {
          detail: { language: languageCode },
        })
      )
    } catch (err) {
      console.error('Error saving translations:', err)
      error.value = 'Failed to save translations'
    } finally {
      saving.value = false
    }
  }

  const goBackToLanguages = () => {
    router.push('/languages')
  }

  // Tree view toggle functions
  const toggleSection = (sectionKey) => {
    expandedSections.value[sectionKey] = !expandedSections.value[sectionKey]
  }

  onMounted(async () => {
    await Promise.all([loadLanguageInfo(), loadTranslations()])
  })
</script>

<style scoped>
  .option-container {
    margin-bottom: 2rem;
  }

  .section-heading h1 {
    display: flex;
    align-items: center;
    font-size: 1.5rem;
    font-weight: 600;
    color: #333;
  }

  .option-icon {
    margin-right: 0.5rem;
  }

  .translation-tree {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 16px;
    background-color: #fafafa;
  }

  .v-card-title h2 {
    margin: 0;
  }

  /* Category header styling */
  .category-header {
    border-bottom: 2px solid #e0e0e0;
    padding-bottom: 8px;
    margin-bottom: 16px;
  }

  .category-header.global {
    border-bottom-color: #1976d2;
  }

  .category-header.pages {
    border-bottom-color: #4caf50;
  }

  .category-header.system {
    border-bottom-color: #ff9800;
  }

  /* Ensure button alignment */
  .d-flex.align-center .v-btn,
  .d-flex.align-center .v-btn-toggle {
    height: 38px;
  }

  /* Styling for sections with changes */
  .section-with-changes {
    border-left: 4px solid #ffc107 !important;
    box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2) !important;
  }

  .section-with-changes .v-card-title {
    background-color: rgba(255, 193, 7, 0.05);
  }
</style>
